import { apiGet } from './apiClient';

export type AdminAccessInfo = {
  hasAdminPanelAccess: boolean;
  isAdmin: boolean;
  isAnalyst: boolean;
  canManageStructure: boolean;
  canEditRecords: boolean;
  canGradeRecords: boolean;
};

export type AdminPermissionsState = AdminAccessInfo & {
  /** Активен UI-режим «как analyst» (только для admin, без смены роли в БД) */
  previewAsAnalyst: boolean;
  /** Может включить/выключить preview (реальный admin) */
  canToggleAnalystPreview: boolean;
};

export const ADMIN_ANALYST_PREVIEW_KEY = 'dc_admin_analyst_preview_v1';

type AdminAccessApiPayload = Partial<AdminAccessInfo> & {
  /** Ответ старого backend до роли analyst */
  isAdmin?: boolean;
  isAnalyst?: boolean;
};

function normalizeAdminAccess(payload: AdminAccessApiPayload | null): AdminAccessInfo | null {
  if (!payload) return null;

  if (payload.hasAdminPanelAccess === true) {
    const canEditRecords = payload.canEditRecords ?? Boolean(payload.isAdmin || payload.isAnalyst);
    const canGradeRecords = payload.canGradeRecords ?? Boolean(payload.isAdmin || payload.isAnalyst);
    return {
      hasAdminPanelAccess: true,
      isAdmin: Boolean(payload.isAdmin),
      isAnalyst: Boolean(payload.isAnalyst),
      canManageStructure: payload.canManageStructure ?? Boolean(payload.isAdmin),
      canEditRecords,
      canGradeRecords,
    };
  }

  if (payload.isAdmin === true) {
    return {
      hasAdminPanelAccess: true,
      isAdmin: true,
      isAnalyst: false,
      canManageStructure: true,
      canEditRecords: true,
      canGradeRecords: true,
    };
  }

  return null;
}

export function readAnalystPreviewEnabled(): boolean {
  try {
    return localStorage.getItem(ADMIN_ANALYST_PREVIEW_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeAnalystPreviewEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ADMIN_ANALYST_PREVIEW_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function buildAdminPermissionsState(
  access: AdminAccessInfo,
  previewEnabled: boolean,
): AdminPermissionsState {
  const canToggle = access.isAdmin;
  const activePreview = canToggle && previewEnabled;
  return {
    hasAdminPanelAccess: access.hasAdminPanelAccess,
    isAdmin: access.isAdmin,
    isAnalyst: activePreview || access.isAnalyst,
    canManageStructure: activePreview ? false : access.canManageStructure,
    canEditRecords: activePreview ? true : access.canEditRecords,
    canGradeRecords: activePreview ? true : access.canGradeRecords,
    previewAsAnalyst: activePreview,
    canToggleAnalystPreview: canToggle,
  };
}

export async function fetchAdminAccess(): Promise<AdminAccessInfo | null> {
  try {
    const payload = await apiGet<AdminAccessApiPayload>('/admin/access');
    return normalizeAdminAccess(payload);
  } catch {
    return null;
  }
}

export async function checkAdminAccess(): Promise<boolean> {
  const info = await fetchAdminAccess();
  return info?.hasAdminPanelAccess === true;
}
